import { useState, useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { plannerApi, PlannerSearchQuery, PlannerSearchView } from '@/entities/planner';

export interface UsePlannerSearchReturn {
  query: PlannerSearchQuery | null;
  data: PlannerSearchView | null;
  loading: boolean;
  error: Error | null;
  search: (searchQuery: PlannerSearchQuery) => Promise<void>;
  setQuery: (query: PlannerSearchQuery) => void;
}

const defaultQuery: PlannerSearchQuery = {
  filterGroups: [],
  sortBy: 'name',
  sortOrder: 'ASC',
  pageSize: 20,
};

const DEDUPING_INTERVAL_MS = 500;

function createSearchKey(query: PlannerSearchQuery): string {
  return JSON.stringify(query);
}

export function usePlannerSearch(initialQuery?: PlannerSearchQuery): UsePlannerSearchReturn {
  const { mutate } = useSWRConfig();
  const [query, setQueryState] = useState<PlannerSearchQuery | null>(initialQuery ?? defaultQuery);
  const [searchKey, setSearchKey] = useState<string | null>(null);

  const { data, error, isLoading } = useSWR<PlannerSearchView>(
    searchKey ? ['planners-search', searchKey] : null,
    () => plannerApi.getPlannersListAsSearchView(),
    {
      dedupingInterval: DEDUPING_INTERVAL_MS,
      keepPreviousData: true,
    }
  );

  const search = useCallback(
    async (searchQuery: PlannerSearchQuery): Promise<void> => {
      const key = createSearchKey(searchQuery);
      setSearchKey(key);
      setQueryState(searchQuery);
      await mutate(['planners-search', key], () => plannerApi.getPlannersListAsSearchView(), {
        revalidate: true,
      });
    },
    [mutate]
  );

  const setQuery = useCallback((q: PlannerSearchQuery) => {
    setQueryState(q);
  }, []);

  return {
    query,
    data: data ?? null,
    loading: isLoading,
    error: error
      ? error instanceof Error
        ? error
        : new Error('검색 중 오류가 발생했습니다.')
      : null,
    search,
    setQuery,
  };
}
